import React, { useState } from "react";
import { TriggerStepCreate } from "@dots-projects/types";
import { useForm, Controller } from "react-hook-form";
import produce from "immer";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import invariant from "ts-invariant";
import { StepFormWithCommonParts } from "./TriggerStepForm";
import { saveDraft, updateStep } from "./reducer/actionCreators";
import TriggerEditorChannelSelector from "./TriggerEditorChannelSelector";
import { FormLabel } from "./FormLabel";
import { Input } from "./Input";
import { ThreadAutoArchiveDuration } from "discord-api-types/v10";
/**
 * Renders the form for a "Create Thread" step. When the form is submitted, generate the step data and publish an update action to add a new step to the trigger flow.
 */
export function StepFormCreateThread({ step }: { step: TriggerStepCreate }) {
  invariant(step.type === "create_thread");

  const [errorSelect, setErrorSelect] = useState(
    ["Select channel", "$channelId"].includes(step.configs.channelId)
  );

  const defaultValues = {
    name: step.name || "Create a thread",
    channelId: step.configs.channelId,
    archiveDurationMinutes:
      step.configs.archiveDurationMinutes || ThreadAutoArchiveDuration.OneWeek,
    title: step.configs.title,
  };

  const form = useForm({
    defaultValues,
    mode: "onChange",
  });
  const { handleSubmit, register, control } = form;
  const { state, dispatch, onPersist } = useTriggerEditorStateDispatch();
  const readOnly = !state.trigger.current.isDraft;

  const onSubmit = (data: any) => {
    // This uses the immer library to create a new copy of step (with updates)
    // without affecting the original step.
    const newStep = produce(step, (stepDraft) => {
      stepDraft.name = data.name;
      stepDraft.configs.channelId = data.channelId;
      stepDraft.configs.archiveDurationMinutes = data.archiveDurationMinutes;
      stepDraft.configs.title = data.title;
      // We only support public thread for now, eventually we'll support private threads too.
      stepDraft.configs.type = "public";
    });
    dispatch(updateStep(newStep));
    dispatch(saveDraft(onPersist));
  };
  return (
    <StepFormWithCommonParts
      onSubmit={handleSubmit(onSubmit)}
      form={form}
      step={step}
    >
      <Controller
        control={control}
        name="channelId"
        rules={{
          required: true,
          validate: (channelId) => {
            return (
              channelId !== undefined &&
              channelId !== "Select channel" &&
              channelId !== "$channelId"
            );
          },
        }}
        render={({ field }) => (
          <TriggerEditorChannelSelector
            readOnly={readOnly}
            field={field}
            errorSelect={errorSelect}
            trigger={state.trigger.current}
            label="Channel to create thread in"
            step={step}
            showRefreshButton
          />
        )}
      />
      <FormLabel label="Title">
        <div className={"w-full"}>
          <Input className={"w-full"} {...register("title")} />
        </div>
      </FormLabel>
      <FormLabel label="Time to hide after inactivity">
        <select {...register("archiveDurationMinutes")} className={"w-full"}>
          <option value={ThreadAutoArchiveDuration.OneHour}>1 Hour</option>
          <option value={ThreadAutoArchiveDuration.OneDay}>24 Hour</option>
          <option value={ThreadAutoArchiveDuration.ThreeDays}>3 Days</option>
          <option value={ThreadAutoArchiveDuration.OneWeek}>1 Week</option>
        </select>
      </FormLabel>
    </StepFormWithCommonParts>
  );
}
